import { AsyncPipe, Location, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AuthService } from '@interacta-shared/data-access-auth';
import { InstalledFeaturesService } from '@interacta-shared/data-access-configuration';
import { LicenseRestrictionService } from '@interacta-shared/data-access-license';
import { Theme } from '@interacta-shared/data-access-theme';
import { AudioRecorderService } from '@interacta-shared/feature-audio-record';
import {
    ApplyPipe,
    ButtonComponent,
    IconButtonComponent,
    ResponsiveButtonComponent,
} from '@interacta-shared/ui';
import { IconComponent } from '@interacta-shared/ui-icons';
import { filterMap } from '@interacta-shared/util';
import { LicenceType } from '@interacta-shared/util-common';
import {
    selectCommunityList,
    selectCommunityTree,
} from '@interacta/data-access-community-tree';
import { openILabAddSourceTypeDialog } from '@interacta/data-access-i-lab';
import { ICommunity } from '@interacta/util-community';
import { AIActions } from '@modules/ai/store/ai.actions';
import { postCreate } from '@modules/app-routing/routes';
import { CommunityActions } from '@modules/communities/store';
import * as CommunitySelectors from '@modules/communities/store/community/community.selectors';
import {
    selectAdvancedCommunityResearchOpened,
    selectRecentCommunities,
} from '@modules/communities/store/community/community.selectors';
import { UserCapabilitiesService } from '@modules/core/services/user-capabilites.service';
import { AppSelectors, AppState } from '@modules/core/store';
import { InvisibleScrollComponent } from '@modules/shared-v2/components/invisible-scroll/invisible-scroll.component';
import {
    CreateButtonDisabledSections,
    CreateButtonSections,
    SectionSelected,
    SidebarSection,
    SidebarSectionId,
    SidebarWorkspace,
} from '@modules/sidebar/models/sidebar.model';
import {
    attemptedCommunityId,
    attemptedRoute,
    CommunityAdvancedSection,
    createNewButtonLabel,
    filterHiddenSections,
    filterWorkspaces,
    getSelectedSidebarSection,
    sidebarNotScrollableSections,
    sidebarScrollableSectionItems,
} from '@modules/sidebar/models/sidebar.utils';
import { SidebarService } from '@modules/sidebar/services/sidebar.service';
import { selectShowDashboardLink } from '@modules/sidebar/store/sidebar.selectors';
import { StateService } from '@modules/state/services/state.service';
import { UIStateService } from '@modules/state/services/ui-state.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    switchMap,
} from 'rxjs/operators';
import { CommunitySelectionDialogComponent } from '../community-selection-dialog/community-selection-dialog.component';
import { SidebarAdvancedCommunityResearchComponent } from '../sidebar-advanced-community-research/sidebar-advanced-community-research.component';
import { SidebarLogoComponent } from '../sidebar-logo/sidebar-logo.component';
import { SidebarSectionComponent } from '../sidebar-section/sidebar-section.component';
import { SidebarWorkspaceListComponent } from '../sidebar-workspace-list/sidebar-workspace-list.component';

const COMMUNITY_NUMBER_LIMIT = 10;

@Component({
    selector: 'interacta-sidebar',
    templateUrl: './sidebar.component.html',
    styles: `
        .create-new-button {
            margin-top: 0.25rem /* 4px */;
            margin-bottom: 0.25rem /* 4px */;
            margin-left: 0.75rem /* 12px */;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        ApplyPipe,
        TranslatePipe,
        ResponsiveButtonComponent,
        SidebarSectionComponent,
        SidebarWorkspaceListComponent,
        SidebarLogoComponent,
        SidebarAdvancedCommunityResearchComponent,
        CommunitySelectionDialogComponent,
        InvisibleScrollComponent,
        MatTooltip,
        NgClass,
        IconComponent,
        IconButtonComponent,
        ButtonComponent,
    ],
})
export class SidebarComponent implements OnInit {
    private readonly workspacesUiService = inject(UIStateService);
    private readonly router = inject(Router);
    private readonly location = inject(Location);

    private readonly installedFeaturesService = inject(
        InstalledFeaturesService,
    );
    private readonly store = inject(Store<AppState>);
    private readonly userCapabilitiesService = inject(UserCapabilitiesService);
    private readonly authService = inject(AuthService);
    private readonly licenseRestrictionService = inject(
        LicenseRestrictionService,
    );
    private readonly sidebarService = inject(SidebarService);
    private readonly appState = inject(StateService);
    private readonly destroyRef = inject(DestroyRef);
    readonly recorderService = inject(AudioRecorderService);

    readonly currentTheme = input.required<Theme>();
    readonly expanded = input(false);

    readonly showDashboardLink$ = this.store.select(selectShowDashboardLink);
    readonly operativityCommunities = this.store.selectSignal(
        CommunitySelectors.selectOperativityCommunities,
    );
    readonly showCommunitySelectionDialog =
        this.sidebarService.communitySelectionForCreateDialogSettings;
    readonly recentCommunities = this.store.selectSignal(
        selectRecentCommunities,
    );
    private readonly currentCommunity = this.store.selectSignal(
        CommunitySelectors.selectCurrentCommunity,
    );
    private readonly communityList =
        this.store.selectSignal(selectCommunityList);
    private readonly attemptedCommunity = toSignal(
        this.store
            .select(AppSelectors.selectRouteState)
            .pipe(
                map((route) =>
                    route
                        ? attemptedCommunityId(route, this.location.path())
                        : null,
                ),
            ),
        { initialValue: null },
    );

    /**
     * A blocked navigation leaves the store on the community the user came from,
     * which would keep that one marked as current while the block names another.
     */
    readonly selectedCommunity = computed(() => {
        const attempted = this.attemptedCommunity();

        return attempted == null
            ? this.currentCommunity()
            : (this.communityList().find((c) => c.id === attempted) ?? null);
    });

    /**
     * advancedCommunitySidebar = true means that communities are displayed in submenu
     * advancedCommunitySidebar = false means that communities are displayed under
     *      community menu item and there isn't a community submenu
     */
    advancedCommunitySidebar$!: Observable<boolean>;
    readonly showAdvancedCommunityResearch = this.store.selectSignal(
        selectAdvancedCommunityResearchOpened,
    );
    readonly isAnimatingAdvancedTab = signal(false);
    readonly sectionSelected$ = combineLatest([
        this.store.select(AppSelectors.selectRouteState),
        this.showDashboardLink$,
    ]).pipe(
        filterMap(([route, showDashboardLink]) =>
            route != null ? { route, showDashboardLink } : undefined,
        ),
        map(({ route, showDashboardLink }) => {
            const attempted = attemptedRoute(route, this.location.path());

            return {
                sectionId: getSelectedSidebarSection(
                    attempted,
                    showDashboardLink,
                ),
                route: attempted,
            };
        }),
        shareReplay(),
        takeUntilDestroyed(),
    );

    readonly sectionSelected = toSignal(this.sectionSelected$);

    readonly createNewDisabled = computed(() =>
        this.disableCreateNewButton(
            this.sectionSelected(),
            this.operativityCommunities()?.length ?? 0,
            this.selectedCommunity(),
        ),
    );

    sidebarWorkspaces$!: Observable<SidebarWorkspace[]>;
    operativityWorkspaces$!: Observable<SidebarWorkspace[]>;
    workspacesUI$ = this.appState.uiState.workspacesUI.onDataChange();
    readonly userCapabilities = this.userCapabilitiesService.userCapabilities;

    readonly communityAdvancedSection = CommunityAdvancedSection;

    readonly sidebarNotScrollableSections = sidebarNotScrollableSections;
    readonly createNewButtonLabel = createNewButtonLabel;
    readonly createButtonSections = CreateButtonSections;

    private currentUser = this.authService.currentUserData;

    private insightCommunities = this.store.selectSignal(
        CommunitySelectors.selectInsightsCommunities,
    );

    readonly otherSidebarSections = computed<SidebarSection[]>(() => {
        const insightCommunities = this.insightCommunities() ?? [];
        const userCapabilities = this.userCapabilities();

        return sidebarScrollableSectionItems.map((section) => {
            switch (section.id) {
                case 'insights':
                    return {
                        ...section,
                        hidden: insightCommunities.length === 0,
                    };
                case 'people':
                    return {
                        ...section,
                        hidden: !this.currentUser()?.userSettings
                            ?.peopleSectionEnabled,
                    };
                case 'digital_workplace_admin':
                    return {
                        ...section,
                        hidden: !userCapabilities?.manageDigitalWorkplace,
                    };
                case 'i-lab':
                    return {
                        ...section,
                        hidden: !this.iLabEnabled(),
                    };
                default:
                    return section;
            }
        });
    });

    readonly otherVisibleSidebarSections = computed(() =>
        filterHiddenSections(this.otherSidebarSections()),
    );

    readonly aiEnabled = computed(
        () =>
            this.installedFeaturesService.getInstalledFeature(
                'aiGuideCenter',
            )() ?? false,
    );
    readonly iLabEnabled = computed(
        () =>
            this.licenseRestrictionService.hasAdvanced() &&
            !!this.installedFeaturesService.getInstalledFeature('iLab')() &&
            !!this.currentUser()?.licences.some(
                (l) => l.type === LicenceType.I_LAB,
            ),
    );

    ngOnInit(): void {
        const communityTree$ = this.store.select(selectCommunityTree);

        const workspaces$ = communityTree$.pipe(
            map((communityTree) => communityTree?.workspaceList ?? []),
            shareReplay(1),
        );

        this.operativityWorkspaces$ = workspaces$.pipe(
            map((workspaces) =>
                workspaces
                    .map((workspace) =>
                        filterWorkspaces(workspace, '', true, true),
                    )
                    .filter((workspace) => workspace.communities?.length),
            ),
        );

        this.sidebarWorkspaces$ = workspaces$.pipe(
            map((workspaces) =>
                workspaces.filter(
                    (workspace) => workspace.visibleInOrganizationTree,
                ),
            ),
        );

        this.advancedCommunitySidebar$ = communityTree$.pipe(
            map(
                (communityTree) =>
                    communityTree?.communityList.filter(
                        (c) => c.visibleInOrganizationTree,
                    ) ?? [],
            ),
            map((communities) => communities.length > COMMUNITY_NUMBER_LIMIT),
            distinctUntilChanged(),
        );

        this.sectionSelected$
            .pipe(
                switchMap((section) =>
                    this.advancedCommunitySidebar$.pipe(
                        map((canShow) => ({
                            section,
                            isShowing: this.showAdvancedCommunityResearch(),
                            canShow,
                        })),
                    ),
                ),
                filter(({ section, isShowing, canShow }) =>
                    this.computeFilterForToggleSecondarySidebar(
                        isShowing,
                        section.sectionId,
                        'community',
                        canShow,
                    ),
                ),
            )
            .subscribe(() => this.toggleAdvancedCommunityResearch());
    }

    onCollapsedWs(id: number): void {
        this.workspacesUiService.toggleWorkspaceOpen(id);
    }

    toggleAdvancedSidebar(
        sectionId: SidebarSectionId,
        showSpecificSidebar: boolean,
    ): void {
        if (sectionId === 'community') {
            this.toggleAdvancedCommunityResearchAndSwitchFocus(
                showSpecificSidebar,
            );
        }
    }

    toggleAdvancedCommunityResearchAndSwitchFocus(
        showAdvancedCommunityResearch: boolean,
    ): void {
        this.toggleAdvancedCommunityResearch();
        requestAnimationFrame(() => {
            document
                .getElementById(
                    showAdvancedCommunityResearch
                        ? 'community'
                        : 'advancedCommunityResearch',
                )
                ?.focus();
        });
    }

    handleCreateClick(selectedCommunity: ICommunity | null): void {
        if (selectedCommunity) {
            this.router.navigate([postCreate, selectedCommunity.id]);
        } else {
            this.sidebarService
                .openCommunitySelectionForCreateDialog()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((selected) => {
                    this.router.navigate([postCreate, selected.id]);
                });
        }
    }

    disableCreateNewButton(
        sectionSelected: SectionSelected | undefined,
        operativityCommunitiesLength: number,
        selectedCommunity: ICommunity | null,
    ): boolean {
        if (!sectionSelected) {
            return true;
        }

        if (CreateButtonDisabledSections.includes(sectionSelected.sectionId)) {
            return true;
        }

        return (
            this.isHomeManageDisabled(
                sectionSelected,
                selectedCommunity,
                operativityCommunitiesLength,
            ) ||
            this.canCreatePostInCommunity(sectionSelected, selectedCommunity)
        );
    }

    private isHomeManageDisabled(
        sectionSelected: SectionSelected,
        selectedCommunity: ICommunity | null,
        operativityCommunitiesLength: number,
    ): boolean {
        return (
            (['home', 'manage'].includes(sectionSelected.sectionId) ||
                (sectionSelected.sectionId === 'community' &&
                    !selectedCommunity)) &&
            !operativityCommunitiesLength
        );
    }

    private canCreatePostInCommunity(
        sectionSelected: SectionSelected,
        selectedCommunity: ICommunity | null,
    ): boolean {
        return (
            sectionSelected.sectionId === 'community' &&
            selectedCommunity != null &&
            !selectedCommunity.capabilities?.createPost
        );
    }

    openAIPanel(): void {
        this.store.dispatch(AIActions.openGuide());
    }

    openILabSourcePicker(): void {
        this.store.dispatch(
            openILabAddSourceTypeDialog({ context: { mode: 'createLab' } }),
        );
    }

    private toggleAdvancedCommunityResearch(): void {
        this.store.dispatch(CommunityActions.toggleAdvancedCommunityResearch());
        this.isAnimatingAdvancedTab.set(true);
    }

    private computeFilterForToggleSecondarySidebar(
        isShowing: boolean,
        sectionId: SidebarSectionId,
        sectionToCheck: SidebarSectionId,
        canShow = true,
    ): boolean {
        return (
            ((isShowing && sectionId !== sectionToCheck) ||
                (!isShowing && sectionId === sectionToCheck)) &&
            canShow
        );
    }
}
